import React from "react";
import Dropzone from "react-dropzone";
import PropTypes from "prop-types";
import { Button, Badge, Spinner, Row, Col, Progress } from "reactstrap";
import ImageCropper from "../../ImageCropper";
import { FaFileUpload } from "react-icons/fa";
import { REACT_APP_COP_SERVER } from "../../../utils/env";
import { useSelector } from "react-redux";
import { upload } from "../../../redux/editor/file/sagas";

const FileUploadPreview = (
  {
    file,
    disablePreview,
    cutting,
    allowedFormats,
    acceptedMIMETypes,
    widgetMode,
    formRatio,
    cropShape,
    forceWhiteBackground,
    minHeight,
    mobileVisible,
    uploadFile,
    handleUpdateSku,
    sku,
    sizeChanged,
  },
  context
) => {
  /**
   * Handle file uploads
   */
  const handleDrop = (acceptedFiles) => {
    const currentSku = sku || handleUpdateSku();
    uploadFile({
      files: acceptedFiles,
      sku: currentSku,
      options: { silentUpload: disablePreview, sizeChanged },
    });
  };

  const { uploadProgress, cropping } = useSelector((state) => ({
    uploadProgress: state.artworks.uploadProgress,
    cropping: state.artworks.cropping,
  }));

  /**
   * Upload Section for File Upload
   */
  const renderUploadSection = (getRootProps, getInputProps) => (
    <div
      {...getRootProps()}
      className={`fileupload-dropzone ${
        disablePreview ? "fileupload-dropzone--disable-preview" : ""
      }`}
      style={minHeight ? { minHeight: "auto" } : {}}
    >
      {mobileVisible && (
        <div className="border rounded text-left p-2 pointer-primary mx-md-5">
          <Row>
            <Col xs={10}>
              <strong>{context.t("Upload Now")}</strong>
              <p className="text-secondary mb-0">
                {context.t("Upload design and crop if required")}
              </p>
            </Col>
            <Col
              xs={2}
              className="d-flex flex-column justify-content-center h5 mb-0"
            >
              <FaFileUpload />
            </Col>
          </Row>
        </div>
      )}
      <input {...getInputProps()} />
      {!mobileVisible && (
        <>
          <p className="h6 mt-4">
            {context.t(
              "Please upload your design. We support these file formats:"
            )}
          </p>
          <div className="fileupload-filetypes h4">
            {allowedFormats.map((format, idx) => (
              <Badge key={idx}>{format}</Badge>
            ))}
          </div>
          <div
            className="fileupload-dropzone-button"
            style={minHeight ? { padding: "1rem" } : {}}
          >
            <Button onClick={(e) => e.preventDefault()} color="success">
              {context.t("Upload File")}
            </Button>
            <p className="text-remark pt-2">
              {context.t("You can also drag & drop your files here.")}
            </p>
          </div>
        </>
      )}
    </div>
  );

  return (
    <>
      <div
        id="stage"
        className={`fileupload ${
          mobileVisible && (!file || !cropping?.active)
            ? "fileupload--mobile-visible"
            : ""
        } ${file ? "fileuploaded" : ""}`}
        style={{ minHeight: minHeight || "400px" }}
      >
        <Dropzone
          accept={acceptedMIMETypes}
          onDrop={handleDrop}
          rejectClassName={"fileupload-dropzone--reject"}
          activeClassName="fileupload-dropzone--active"
        >
          {({ getRootProps, getInputProps }) => (
            <>
              {uploadProgress === 0 &&
                !file?.filetype &&
                !widgetMode &&
                renderUploadSection(getRootProps, getInputProps)}

              {uploadProgress !== 0 && (!file?.preview || !file?.filetype) && (
                <p className="text-center">
                  <Progress
                    animated
                    color="success"
                    value={
                      (uploadProgress +
                        (file?.preview ? 10 : 0) +
                        (file?.filetype ? 10 : 0)) /
                      1.2
                    }
                    style={{ height: "1rem" }}
                  >
                    {Math.round(
                      (uploadProgress +
                        (file?.preview ? 10 : 0) +
                        (file?.filetype ? 10 : 0)) /
                        1.2
                    )}{" "}
                    %
                  </Progress>
                  {uploadProgress !== 100 &&
                    context.t(
                      "Uploading file, should be ready in a few moments..."
                    )}
                  {uploadProgress === 100 && !file?.preview && (
                    <>{context.t("Preview Image Generation")}</>
                  )}
                  {uploadProgress === 100 &&
                    !file?.filetype &&
                    context.t("Analyzing File")}
                </p>
              )}

              {!cropping?.active && file?.preview && !widgetMode && (
                <img
                  src={
                    file.preview.startsWith("https")
                      ? file.preview
                      : `${REACT_APP_COP_SERVER}${file.preview}`
                  }
                  className={`fileupload-dropzone-preview ${
                    cutting === "rectangular"
                      ? "fileupload-dropzone-preview--rectangular"
                      : ""
                  }`}
                  alt="fileupload preview"
                  style={
                    forceWhiteBackground ? { backgroundColor: "#fff" } : {}
                  }
                />
              )}

              {file?.uploadedSilent && (
                <p>{context.t("Your file was uploaded successfully.")}</p>
              )}
            </>
          )}
        </Dropzone>
        {cropping?.active && file?.preview && (
          <div style={{ minHeight: "250px", width: "100%" }}>
            <ImageCropper
              image={file.preview}
              ratio={isNaN(formRatio) ? 1 : formRatio}
              cropShape={cropShape}
            />
          </div>
        )}
      </div>

      {/* {uploadProgress === 100 && !file?.preview && (
        <p className="text-center">
          <Spinner size="sm" color="info" /> {context.t("Preview Image Generation")}
        </p>
      )}
      {uploadProgress === 100 && !file?.width && (
        <p className="text-center">
          <Spinner size="sm" color="info" /> {context.t("Analyzing File")}
        </p>
      )} */}
      {file?.filename && (
        <pre>
          {file?.filename} ({file?.width}x{file?.height}/ {file?.filetype})
        </pre>
      )}
      {file?.warnings?.length > 0 && (
        <div className="my-3 bg-light p-3" style={{ display: "none" }}>
          <span className="h5">
            ⚠️ Beta Feature Notice: We've automatically checked your file and
            found the following warnings for your review:
          </span>
          <p>
            <ul>
              {file?.warnings.map((warning) => (
                <li>{warning}</li>
              ))}
            </ul>
          </p>
        </div>
      )}
    </>
  );
};

FileUploadPreview.propTypes = {
  file: PropTypes.object,
  disablePreview: PropTypes.bool,
  cutting: PropTypes.string,
  allowedFormats: PropTypes.arrayOf(PropTypes.string),
  acceptedMIMETypes: PropTypes.string,
  widgetMode: PropTypes.bool,
  formRatio: PropTypes.number,
  cropShape: PropTypes.string,
  forceWhiteBackground: PropTypes.bool,
  minHeight: PropTypes.string,
  mobileVisible: PropTypes.bool,
  uploadFile: PropTypes.func.isRequired,
  handleUpdateSku: PropTypes.func.isRequired,
  sku: PropTypes.string,
  sizeChanged: PropTypes.bool,
};

/**  define proptype for the 'translation' function  */
FileUploadPreview.contextTypes = {
  t: PropTypes.func,
};

export default FileUploadPreview;
